import { linkify } from './linkify';

// YouTube VIDEO links inside member text — the preview card and the "suggest
// for the library" checkbox both key off these. Channel, playlist and search
// URLs (youtube.com/@IDDHealthMatters) have no single video, so they stay
// plain links: there is nothing to thumbnail without a server-side fetch.

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com']);

export const parseYouTubeVideoId = (href) => {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  let id = null;
  if (host === 'youtu.be' || host === 'www.youtu.be') {
    id = url.pathname.split('/')[1];
  } else if (YOUTUBE_HOSTS.has(host)) {
    if (url.pathname === '/watch') {
      id = url.searchParams.get('v');
    } else {
      const [, kind, value] = url.pathname.split('/');
      if (['shorts', 'live', 'embed'].includes(kind)) id = value;
    }
  }
  return id && VIDEO_ID_RE.test(id) ? id : null;
};

// First YouTube video linked in the text, or null. One card per post: several
// stacked players would bury the conversation the post is for.
export const firstYouTubeVideoId = (...texts) => {
  for (const text of texts) {
    for (const segment of linkify(text)) {
      if (segment.type !== 'link') continue;
      const id = parseYouTubeVideoId(segment.href);
      if (id) return id;
    }
  }
  return null;
};

export const youtubeThumbnail = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
export const youtubeWatchUrl = (id) => `https://www.youtube.com/watch?v=${id}`;

// Title lookup via YouTube's public oEmbed endpoint, which allows cross-origin
// reads (SubmitVideoScreen has used it since 004). Cached per session so a feed
// of posts linking the same video asks once. Resolves to null for private,
// deleted or embed-disabled videos, and on network failure.
const infoCache = new Map();

export const fetchYouTubeInfo = (id) => {
  if (!infoCache.has(id)) {
    const request = fetch(
      `https://www.youtube.com/oembed?url=${encodeURIComponent(youtubeWatchUrl(id))}&format=json`
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => (data?.title
        ? { title: data.title, author: data.author_name ?? null }
        : null))
      .catch(() => null);
    infoCache.set(id, request);
    // A failed lookup shouldn't stick for the whole session; drop it so a
    // later render can try again.
    request.then((info) => { if (!info) infoCache.delete(id); });
  }
  return infoCache.get(id);
};
